// GroundTruth-specific constants for the Sidekick CLW.
//
// Everything here is now a PROJECTION of the bundled field descriptor
// (gtFieldDescriptor.json + gtFieldDescriptor.js), which is itself generated
// from gt_io_ui_field_spec.xlsx by scripts/generateGtFieldDescriptor.js.
// Pure data — no React or context dependencies. Keep this file thin: add a
// projection to gtFieldDescriptor.js rather than a new parallel constant.

import {
  getGtFieldDescriptor,
  gtGroupCols,
  gtGroupKeys,
  gtFieldEditors,
  gtStaticOptions,
  gtNonEditable,
  gtReadOnlyReasons,
  gtRowToAg,
  gtIssueFieldMap,
  gtListFields,
  gtEditableFields,
} from './gtFieldDescriptor';

/** The active descriptor — bundled JSON unless registerGtFieldDescriptor ran. */
export const GT_FIELD_DESCRIPTOR = getGtFieldDescriptor();

/**
 * Resolve a validator issue's `field_name` (or category) to the grid column
 * key it should annotate. Used by buildGtReviewRows + by the issues-bar
 * click-to-jump handler so both surfaces agree on where an issue lives.
 * Projected from descriptor configKey → rowKey pairs.
 */
export const GT_ISSUE_FIELD_MAP = gtIssueFieldMap(GT_FIELD_DESCRIPTOR);

/**
 * GroundTruth column groups — one row per adgroup. Mirrors the design's
 * 7-tab DV360 structure (Overview / Flighting / Budget / Targeting /
 * Creative / Bid / Tracking) so the GT review pane feels parallel to the
 * existing DV360 one. Projected from each field's gridColumns entry.
 */
export const GT_GROUP_COLS = gtGroupCols(GT_FIELD_DESCRIPTOR);

export const GT_GROUP_KEYS = gtGroupKeys(GT_FIELD_DESCRIPTOR);

/**
 * Read-only GT cells: derived/system fields whose value can't be round-tripped
 * back to an adgroup property, projected from editable=false in the descriptor
 * plus the sticky fmt/channel cells. Everything else (mapped via
 * GT_ISSUE_FIELD_MAP plus `name`) is user-editable.
 */
export const GT_NON_EDITABLE = gtNonEditable(GT_FIELD_DESCRIPTOR);

/** Why each read-only cell is read-only — the discipline the other platforms keep. */
export const GT_READ_ONLY_REASONS = gtReadOnlyReasons(GT_FIELD_DESCRIPTOR);

/**
 * Editor type per GT column — text (default), number (currency / numeric),
 * date (YYYY-MM-DD). Projected from each field's `control` entry.
 */
export const GT_FIELD_EDITORS = gtFieldEditors(GT_FIELD_DESCRIPTOR);

/**
 * Fixed option sets for the 'select' editors above — closed enums the
 * pipeline/GT API enforces (bid_type, billability_type, product, …).
 * Projected from each field's staticValues.
 */
export const GT_STATIC_OPTIONS = gtStaticOptions(GT_FIELD_DESCRIPTOR);

/**
 * Row-field → adgroup-property map for write-back. Row keys differ from
 * adgroup keys for a handful of columns (the row builder renames them for
 * brevity); the rest are identity. Projected from descriptor writeBack entries.
 */
export const GT_ROW_TO_AG = gtRowToAg(GT_FIELD_DESCRIPTOR);

/**
 * The GT editable set — descriptor-driven (every ad-group field not marked
 * read-only), NOT column-driven: UAT 0817 removed grid columns whose config
 * values must stay editable through the agent and the issue-bar jump.
 */
export const GT_EDITABLE_FIELDS = gtEditableFields(GT_FIELD_DESCRIPTOR);

/**
 * Per-field add / edit / remove capabilities for GT list-shaped cells,
 * projected from the descriptor (goal 3: no hand-maintained GT_LIST_FIELDS).
 */
export const GT_LIST_FIELDS = gtListFields(GT_FIELD_DESCRIPTOR);

// Placeholder records the pipeline seeds today. The UI replaces them with real
// taxonomy picks (R12 / R13) and the launcher treats them as the no-pick
// fallback rather than a user choice.
export const GT_AUDIENCE_PLACEHOLDER = {
  id: '49124',
  type: 'custom_audience',
  name: 'Audience Targeting Placeholder',
};
export const GT_LOCATION_GROUP_PLACEHOLDER = {
  id: '92006',
  name: 'Placeholder Location Group',
};

/**
 * The display label for one audiences[] record — `name · id · type`, the same
 * order the taxonomy picker shows. Removes and replaces match on this exact
 * label, so it must stay one function for the row builder AND the appliers.
 */
export const gtAudienceLabel = (entry) => {
  const name = String(entry?.name ?? '').trim() || '—';
  const id = String(entry?.id ?? '').trim() || '—';
  const type = String(entry?.type ?? '').trim() || '—';
  return `${name} · ${id} · ${type}`;
};

/** Is an audiences[] record the 49124 placeholder? */
export const isGtAudiencePlaceholder = (entry) => (
  String(entry?.id) === GT_AUDIENCE_PLACEHOLDER.id
  && String(entry?.type) === GT_AUDIENCE_PLACEHOLDER.type
);
